<template>
  <div>
    <nuxt />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

/**
 * 着陆页布局组件 - 屏幕方向检测
 */
export default {
  name: 'LandingLayout',

  data() {
    return {
      screenType: 'unknown', // portrait(竖屏) / standard(标准屏) / landscape(横屏)
      screenRatio: 0, // 屏幕比例 (宽度/高度)
      windowWidth: 0,
      windowHeight: 0,
    }
  },

  computed: {
    ...mapGetters(['title', 'meta', 'copyright', 'grayIndex']),
    version() {
      return this.$config.version
    },
  },

  mounted() {
    this.initScreenDetection()
    this.addResizeListener()

    console.info(`
    __      __________   ________ _____________________ ____________________
   /  \\    /  \\_____  \\  \\_____  \\\\______   \\_   _____//   _____/\\__    ___/
   \\   \\/\\/   //   |   \\  /   |   \\|    |  _/|    __)_ \\_____  \\   |    |
    \\        //    |    \\/    |    \\    |   \\|        \\/        \\  |    |
     \\__/\\  / \\_______  /\\_______  /______  /_______  /_______  /  |____|
          \\/          \\/         \\/       \\/        \\/        \\/           v${this.version}
   `)
    console.info(this.copyright)
  },

  beforeDestroy() {
    this.removeResizeListener()
  },
  methods: {
    initScreenDetection() {
      this.updateScreenInfo()
    },

    updateScreenInfo() {
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight
      this.screenRatio = this.windowWidth / this.windowHeight
      this.screenType = this.detectScreenType()

      // 更新html标签的class
      this.updateHtmlClass()

      // 创建屏幕信息对象
      const newScreenInfo = {
        type: this.screenType,
        ratio: this.screenRatio,
        width: this.windowWidth,
        height: this.windowHeight,
      }

      // 只使用事件系统通知子组件，避免在$root上添加响应式属性
      this.$root.$emit('screen-info-changed', newScreenInfo)
    },

    // 基于屏幕比例判断屏幕类型：≤1.1为竖屏，1.1-1.5为标准屏，>1.5为横屏
    detectScreenType() {
      const { screenRatio } = this
      if (screenRatio <= 0.6) {
        return 'portrait'
      } else if (screenRatio <= 1.3) {
        return 'standard'
      } else {
        return 'landscape'
      }
    },

    // 更新html标签的class
    updateHtmlClass() {
      if (process.client) {
        const htmlElement = document.documentElement
        if (htmlElement) {
          // 移除之前的屏幕类型class
          htmlElement.classList.remove(
            'landing-screen-portrait',
            'landing-screen-standard',
            'landing-screen-landscape'
          )

          // 添加当前的屏幕类型class
          if (this.screenType !== 'unknown') {
            htmlElement.classList.add(`landing-screen-${this.screenType}`)

            // 标准屏同时应用竖屏样式
            if (this.screenType === 'standard') {
              htmlElement.classList.add('landing-screen-portrait')
            }
          }
        }
      }
    },

    addResizeListener() {
      window.addEventListener('resize', this.handleResize)
      window.addEventListener('orientationchange', this.handleOrientationChange)
    },

    removeResizeListener() {
      window.removeEventListener('resize', this.handleResize)
      window.removeEventListener(
        'orientationchange',
        this.handleOrientationChange
      )
    },

    // 防抖处理窗口大小变化
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.updateScreenInfo()
      }, 100)
    },

    // 延迟处理设备方向变化
    handleOrientationChange() {
      setTimeout(() => {
        this.updateScreenInfo()
      }, 300)
    },
  },
  head() {
    const { name } = this.$route
    // landing 布局静态标识：SSR 与客户端都带上，供 CSS 做 landing 专属的 rem 基准/字体/盒模型设置
    const htmlAttrs = { class: 'landing' }

    // 网站置灰控制：着陆页应用灰度滤镜
    if (this.grayIndex) {
      if (name === 'landing') {
        htmlAttrs.style = 'filter: grayscale(100%)'
      } else {
        htmlAttrs.style = ''
      }
    }

    // 添加屏幕类型class到html标签
    let screenClass = ''
    if (this.screenType !== 'unknown') {
      screenClass = `landing-screen-${this.screenType}`

      // 标准屏同时应用竖屏样式
      if (this.screenType === 'standard') {
        screenClass += ' landing-screen-portrait'
      }
    }
    if (screenClass) {
      htmlAttrs.class = `${htmlAttrs.class} ${screenClass}`
    }

    return {
      ...this.title,
      meta: this.meta,
      htmlAttrs,
    }
  },
}
</script>

<style lang="less">
/* rem 基准字号：纯 CSS 媒体查询按视口宽高比设置，SSR 首屏即生效（不依赖 JS 加 class）。
   阈值与 detectScreenType() 保持一致：<=0.6 竖屏、0.6~1.3 标准屏、>1.3 横屏 */
html.landing {
  font-size: (100 / 1080) * 100vh; /* 横屏/桌面 (宽高比 > 1.3) */
}

@media (max-aspect-ratio: 13/10) and (min-aspect-ratio: 6/10) {
  html.landing {
    font-size: (100 / 1920) * 100vh; /* 标准屏 (0.6 < 宽高比 <= 1.3) */
  }
}

@media (max-aspect-ratio: 6/10) {
  html.landing {
    font-size: (100 / 1080) * 100vw; /* 竖屏/手机 (宽高比 <= 0.6) */
  }
}

/* Landing 布局字体设置（SSR 首屏即应用，避免默认字体闪烁） */
html.landing {
  font-family: 'SourceHanSansCN', sans-serif;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* 确保 landing 页面内所有文本使用自定义字体、统一盒模型 */
html.landing * {
  font-family: inherit;
  box-sizing: border-box;
}

html.landing button {
  background-color: transparent;
}

/* 标准屏内容居中 */
@media (max-aspect-ratio: 13/10) and (min-aspect-ratio: 6/10) {
  html.landing body {
    width: 10.8rem;
    margin: 0 auto;
  }
}
</style>
